import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import { Badge, KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';

interface Props {
  trigger: Trigger;
}

export function CompoundV3TriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const marketAddress = useMemo(() => {
    return trigger.context?.market_address || '';
  }, [trigger.context]);

  const chainId = useMemo(() => {
    return trigger.context?.market_chain_id || 1;
  }, [trigger.context]);

  const positionType = useMemo(() => {
    const type = trigger.context?.position_type;
    return type === 'lending' ? 'Lending' : type === 'borrowing' ? 'Borrowing' : '-';
  }, [trigger.context]);

  const networkInfo = useMemo(() => {
    return NETWORKS.find((network) => network.chainId === chainId);
  }, [chainId]);

  const networkDisplay = useMemo(() => {
    if (!networkInfo) return '-';
    const chainIconUrl = getChainIconUrl(networkInfo.chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={networkInfo.name} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{networkInfo.name.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [networkInfo, getChainIconUrl]);

  const marketDisplay = useMemo(() => {
    if (!marketAddress || !networkInfo) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Snapshots of positions in Compound v3 market
        </Text>
        <AddressChip address={marketAddress} chainId={networkInfo.chainId} />
      </div>
    );
  }, [marketAddress, networkInfo]);

  const positionTypeDisplay = useMemo(() => {
    if (positionType === '-') return '-';

    const color = positionType === 'Lending' ? 'success' : 'warning';

    return <Badge variant={color}>{positionType}</Badge>;
  }, [positionType]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value={marketDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Position Type" value={positionTypeDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
